import { useState, useMemo, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from '@tanstack/react-router'
import { Button, Flex, Input, Modal, Paragraph, Text } from '@nula/core-ui'
import { useAuthStore } from '@/stores/use-auth-store'
import { createWorkspace } from '@/lib/workspace/api/api'
import { useWorkspaceStore } from '@/stores/use-workspace-store'
import { StripeElementsProvider } from '@/lib/billing/stripe-provider'
import { SubscribePanel } from '@/lib/billing/subscribe-panel'
import { IntervalToggle } from '@/lib/billing/interval-toggle'
import { TeamPlanSummary } from '@/lib/billing/team-plan-summary'
import { PromoCodeInput, type AppliedPromo, type CheckoutRejection } from '@/lib/billing/promo-code-input'
import { checkoutRejectionFromError } from '@/lib/billing/checkout-rejection'
import type { BillingInterval } from '@/lib/billing/types'
import { isFreeEmailDomain } from '../utils/free-email-domains'
import styles from './create-workspace-modal.module.css'

interface CreateWorkspaceModalProps {
  onClose: () => void
}

export function CreateWorkspaceModal({ onClose }: CreateWorkspaceModalProps) {
  const userEmail = useAuthStore((s) => s.user?.email ?? '')
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [step, setStep] = useState<'name' | 'pay'>('name')
  const [interval, setInterval] = useState<BillingInterval>('month')
  const [planCode, setPlanCode] = useState<string>('teams_pro')
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromo | null>(null)
  const [checkoutRejection, setCheckoutRejection] = useState<CheckoutRejection | null>(null)
  const rejectionNonce = useRef(0)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [newWorkspaceId, setNewWorkspaceId] = useState<string | null>(null)
  const refreshFromServer = useWorkspaceStore((s) => s.refreshFromServer)
  const switchToWorkspace = useWorkspaceStore((s) => s.switchToWorkspace)

  const userDomain = userEmail.includes('@') ? userEmail.split('@')[1] : ''
  const showPrefillNotice = useMemo(
    () => Boolean(userDomain && !isFreeEmailDomain(userDomain)),
    [userDomain],
  )

  const handleSubmit = async () => {
    if (!name.trim()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      // Team creation returns the checkout clientSecret in the SAME response
      // (design §11.5 #7) — the team is not usable until paid, so advance to
      // the embedded payment step instead of switching to it now.
      const { workspace, clientSecret: secret } = await createWorkspace({
        name: name.trim(),
        type: 'team',
        interval,
        planCode,
        promotionCode: appliedPromo?.code,
      })
      setNewWorkspaceId(workspace.id)
      setClientSecret(secret)
      setStep('pay')
      setSubmitting(false)
    } catch (err) {
      console.error('[CreateWorkspaceModal] create failed:', err)
      Sentry.captureException(err)
      rejectionNonce.current += 1
      const rejection = checkoutRejectionFromError(err, rejectionNonce.current)
      if (rejection) {
        setCheckoutRejection(rejection)
      } else {
        setSubmitError("Couldn't create workspace. Please try again.")
      }
      setSubmitting(false)
    }
  }

  const handlePaid = async () => {
    if (!newWorkspaceId) return
    await refreshFromServer()
    // switchToWorkspace requires a navigate callback — passing only the
    // workspaceId would crash with `opts.navigate is not a function`.
    await switchToWorkspace(newWorkspaceId, (path) => navigate({ to: path }))
    onClose()
  }

  if (step === 'pay' && clientSecret) {
    return (
      <Modal open onClose={onClose} title="Subscribe to activate your team" footer={null} centered>
        <StripeElementsProvider clientSecret={clientSecret}>
          <SubscribePanel clientSecret={clientSecret} onSuccess={() => void handlePaid()} />
        </StripeElementsProvider>
      </Modal>
    )
  }

  const footer = (
    <>
      <Button variant="text" onClick={onClose}>Cancel</Button>
      <Button variant="primary" pressEffect="glow" onClick={handleSubmit} disabled={!name.trim() || submitting} loading={submitting}>
        {submitting ? 'Creating…' : 'Create'}
      </Button>
    </>
  )

  return (
    <Modal open onClose={onClose} title="Create team workspace" footer={footer} centered>
      <Flex vertical className={styles.body}>
        <Text className={styles.fieldLabel}>Workspace name</Text>
        <Input
          aria-label="Workspace name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          label="Your team"
          autoFocus
        />

        <IntervalToggle value={interval} onChange={setInterval} />

        <Flex vertical gap="sm" role="radiogroup" aria-label="Team plan">
          <TeamPlanSummary
            planCode="teams_plus"
            interval={interval}
            selected={planCode === 'teams_plus'}
            onSelect={setPlanCode}
          />
          <TeamPlanSummary
            planCode="teams_pro"
            interval={interval}
            selected={planCode === 'teams_pro'}
            onSelect={setPlanCode}
            recommended
          />
        </Flex>

        <PromoCodeInput
          interval={interval}
          rejectReferrals
          onApplied={setAppliedPromo}
          onRemoved={() => setAppliedPromo(null)}
          checkoutRejection={checkoutRejection}
        />

        <Text size="sm" type="secondary" className={styles.seatNote}>
          You&apos;ll start with one seat. Each member you invite adds a seat, billed
          automatically at the per-seat price.
        </Text>

        {showPrefillNotice && (
          <Paragraph className={styles.prefillNotice}>
            Anyone with @{userDomain} email will be able to join this workspace via invite. You can change this later in Workspace settings.
          </Paragraph>
        )}

        {submitError && (
          <Text type="danger">{submitError}</Text>
        )}
      </Flex>
    </Modal>
  )
}
